import React, { FormEvent, useCallback, useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { PAYMENT_METHOD_LABELS, PaymentMethod } from '../types/invoice';

const METHODS: PaymentMethod[] = ['cash', 'card', 'eft', 'medical_aid', 'other'];

interface Props {
  invoiceId: string;
  balance: number;
  onClose: () => void;
  onRecorded: () => void;
}

export function RecordPaymentModal({ invoiceId, balance, onClose, onRecorded }: Props) {
  const { post } = useApi();
  const [amount, setAmount] = useState(balance.toFixed(2));
  const [method, setMethod] = useState<PaymentMethod>('eft');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await post(`/invoices/${invoiceId}/payments`, {
        amount: numAmount,
        method,
        reference,
        notes,
      });
      if (result.success) onRecorded();
      else setError(result.error || 'Failed to record payment');
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.error) setError(err.response.data.error);
      else setError('Failed to record payment');
    } finally { setSubmitting(false); }
  }, [invoiceId, amount, method, reference, notes, post, onRecorded]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Record Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          <div className="text-sm text-gray-600">Outstanding balance: <strong>R {balance.toFixed(2)}</strong></div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (R)</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
            <select value={method} onChange={e => setMethod(e.target.value as PaymentMethod)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
              {METHODS.map(m => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
            <input type="text" value={reference} onChange={e => setReference(e.target.value)}
              placeholder="Transaction ref, receipt #, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Recording...</> : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
